import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef, Component,
  DOCUMENT,
  ElementRef,
  OnDestroy, OnInit, Renderer2,
  effect,
  inject,
  signal
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, ReplaySubject, Subject, of } from 'rxjs';
import { concatMap, debounceTime, distinctUntilChanged, filter, map, pairwise, skip, startWith, takeUntil, throttleTime } from 'rxjs/operators';
import { AnnouncementBarComponent } from "src/app/components/announcement-bar/announcement-bar.component";
import { BannerCarouselComponent } from "src/app/components/banner-carousel/banner-carousel.component";
import { FilterSelection } from 'src/app/components/filters/filters.component';
import { GlobalFiltersService } from 'src/app/components/filters/services/global-filters.service';
import { ResetFiltersService } from 'src/app/components/product-filters/reset-filters.service';
import { CategoryLayoutType, DynamicPage } from 'src/app/core/enums/dynamic-page.enum';
import { SUGGESTION_KEY } from 'src/app/core/enums/suggestion-search-key.enum';
import { Announcement, AnnouncementBarPage, CategoryLayoutData, CategoryLayoutStyle } from 'src/app/core/interfaces/store-configuration.interface';
import { ListParams } from 'src/app/core/models/list-params.model';
import { AdsManagementService } from 'src/app/core/services/adsManagement/ads-management.service';
import { AnnouncementsService } from 'src/app/core/services/announcements/announcements.service';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { ConversionTrackingService } from 'src/app/core/services/conversion/conversion-tracking.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { GlobalScrollService } from 'src/app/core/services/global-scroll/global-scroll.service';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { SeoService } from 'src/app/core/services/seo/seo.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import {
  createDefaultAnnouncement,
  createDefaultCategoryLayoutData
} from 'src/app/core/utilities/type-defaults.util';
import { PrimaryViewerPanelComponent } from "src/app/home/components/dynamic-template/primary-viewer-panel/primary-viewer-panel.component";
import { PublishStatus } from 'src/app/home/models/home-content.model';
import { HomeContentType } from 'src/app/home/models/home.model';
import { BannerLayoutsComponent } from "src/app/products/components/banner-layouts/banner-layouts.component";
import { Filters, PAGE_SIZE, ProductsLayoutComponent, SORT_BY } from 'src/app/products/components/products-layout/products-layout.component';
import { MappedProductListingSearch } from 'src/app/products/models/product-listing.model';
import { CategorySection } from 'src/app/products/models/product.model';
import { ProductFilterType } from 'src/app/products/services/filters-adapter.service';
import { ProductsPaginationService } from 'src/app/products/services/products-pagination.service';
import { ProductsRepositoryService } from 'src/app/products/services/products-repository.service';
import { LinkHandler } from 'src/app/shared/class/link-handler.class';
import { LAYOUT_TYPE, MOBILE_GRID_LAYOUT } from '../../enum/category-page.enum';
import { ListingPageService } from '../../services/listing-page.service';
import { AZFilterComponent } from '../a-z-filter/a-z-filter.component';
import { BreadCrumb, BreadcrumbNavigationComponent } from '../breadcrumb-navigation/breadcrumb-navigation.component';
import { CategoryPageLayout1Component } from '../category-page-layout-1/category-page-layout-1.component';
import { CategoryPageLayout2Component } from '../category-page-layout-2/category-page-layout-2.component';
import { CategoryPageLayout3Component } from '../category-page-layout-3/category-page-layout-3.component';
import { CategoryPageShimmerComponent } from '../category-page-shimmer/category-page-shimmer.component';
import { GridViewLayout } from '../sorting-layout-1/sorting-layout-1.component';
@Component({
  standalone: true,
  selector: 'app-category-layout',
  templateUrl: './category-layout.component.html',
  styleUrls: ['./category-layout.component.scss'],
  imports: [CommonModule, CategoryPageShimmerComponent, CategoryPageLayout1Component, CategoryPageLayout2Component, CategoryPageLayout3Component, BreadcrumbNavigationComponent, AZFilterComponent, AnnouncementBarComponent, BannerCarouselComponent, PrimaryViewerPanelComponent, BannerLayoutsComponent, ProductsLayoutComponent]
})
export class CategoryLayoutComponent extends LinkHandler implements OnInit, AfterViewInit, OnDestroy {
  announcement!: Announcement;
  override page: DynamicPage = DynamicPage.CATEGORY;

  // Auto-detect page type from route if not provided as input
  private detectedPage!: DynamicPage;

  // Getter that returns either input page or detected page
  get currentPage(): DynamicPage {
    return this.detectedPage || this.page;
  }

  // Injected services
  private platformService = inject(PlatformService);
  private cdr = inject(ChangeDetectorRef);
  private globalFiltersService = inject(GlobalFiltersService);
  private deviceDetectionService = inject(DeviceDetectionService);
  private globalScrollService = inject(GlobalScrollService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private productsRepositoryService = inject(ProductsRepositoryService);
  private productsPaginationService = inject(ProductsPaginationService);
  private resetFiltersService = inject(ResetFiltersService);
  private seoService = inject(SeoService);
  private listingPageService = inject(ListingPageService);
  private conversionTrackingService = inject(ConversionTrackingService);
  private cartRepositoryService = inject(CartRepositoryService);
  private adsManagementService = inject(AdsManagementService);
  private document = inject<Document>(DOCUMENT);
  public override browserApiService = inject(BrowserApiService);
  public route = inject(ActivatedRoute);
  public announcementsService = inject(AnnouncementsService);
  public override renderer = inject(Renderer2);
  public override elementRef = inject(ElementRef);
  public override router = inject(Router);
  public override overlayService = inject(OverlayService);

  breadCrumbInfo$ = this.listingPageService.breadCrumbInfo;
  alphabetFilterInfo$ = this.listingPageService.alphabetFilterInfo;
  private timeoutIds: Array<number | ReturnType<typeof setTimeout>> = [];
  filters = new BehaviorSubject<Filters | null>(null);
  filters$ = this.filters.asObservable();

  // Use signal-based services where available
  aggregations = this.globalFiltersService.aggregations;
  currentAppliedFilters = this.globalFiltersService.currentAppliedFilters;
  previewTemplate$ = this.listingPageService.previewTemplate;
  // emptyPreviewTemplate$ = this.listingPageService.emptyPreviewTemplate$;
  // Convert breadcrumbData to signal for reactive passing to child components
  private _breadcrumbData = signal<BreadCrumb[]>([]);
  readonly breadcrumbData = this._breadcrumbData.asReadonly();
  sortBy$ = of(SORT_BY);
  sortBy = new UntypedFormControl('');
  categoryName = "";
  brandName$!: Observable<string>;
  collectionTitle$!: Observable<string>;
  selectedCategoryId = signal<string>("");
  ssrLoader = true;
  isBrowser = false;
  pageMasking$ = this.cartRepositoryService.pageMasking;

  HomeContentType = HomeContentType;
  PublishStatus = PublishStatus;

  categoryBanners: CategorySection[] = [];

  get categoryWebTopBanner() {
    return this.categoryBanners?.[0]?.banners?.find((banner) => banner.type === 'web' && banner.position === 'top')?.banner_image_url;
  }
  get categoryWebLeftBanner() {
    return this.categoryBanners?.[0]?.banners?.find((banner) => banner.type === 'web' && banner.position === 'left')?.banner_image_url;
  }
  get categoryMobileTopBanner() {
    return this.categoryBanners?.[0]?.banners?.find((banner) => banner.type === 'mobile' && banner.position === 'top')?.banner_image_url;
  }


  // Convert to signals for better performance
  loading = signal<boolean>(false);
  pageLoading = signal<boolean>(true); // Show shimmer from the beginning until first API call completes
  listLoading = signal<boolean>(false);
  pageNumber = signal<number>(0);
  fetchedProducts = signal<MappedProductListingSearch>({ _scroll_id: '', filter: [], hits: { hits: [], max_score: 0, total: { value: 0, relation: 'eq' }, _scroll_id: '' } });

  private listExhausted$: Subject<boolean> = new Subject();
  private isInitializing = true; // Flag to prevent effect from running during initial setup
  private isInitialLoad = true; // Flag to prevent scroll pagination during initial load
  private lastScrollY = 0; // Track last scroll position to detect real user scrolls
  private isUserScrolling = false; // Flag to track if scroll is from user action
  listExhausted = false;

  alphabetFilterControl = new FormControl();

  salesChannelId = this.storeConfigurationRepositoryService.salesChannelId;
  showOutOfStockProducts = this.storeConfigurationRepositoryService.showOutOfStockProducts();
  layoutPageConfiguration!: any;
  // selectedCategoryLayout!: LAYOUT_TYPE;

  private _categoryLayoutData = signal<CategoryLayoutData>(createDefaultCategoryLayoutData());
  readonly categoryLayoutData = this._categoryLayoutData.asReadonly();


  categoryLayoutStyles!: CategoryLayoutStyle;
  LAYOUT_TYPE = LAYOUT_TYPE;
  gridViewLayout = GridViewLayout;
  MOBILE_GRID_LAYOUT = MOBILE_GRID_LAYOUT;

  // Signal for searched value - only shows when on search route
  searchedValue = signal<string>('');

  // Use DeviceDetectionService for mobile detection
  isMobile$ = this.deviceDetectionService.isMobile;
  // @Output() alphabetFilterEvent$ = new EventEmitter<string>();

  public destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  CategoryLayoutType = CategoryLayoutType;
  constructor() {
    super();
    this.isBrowser = this.platformService.isBrowser();
    if (this.isBrowser) {
      this.ssrLoader = false;
    }

    // Auto-detect page type from route
    this.detectPageType();

    // Move effects to constructor where injection context is available
    effect(() => {
      const filters = this.globalFiltersService.currentAppliedFilters();

      // Skip effect during initial setup to prevent duplicate API calls
      if (this.isInitializing) {
        return;
      }

      // Always apply filters when they change, including when clearing (empty array)
      this.applyFilters(filters);
    });

    // Effect for breadcrumb data when categoryId changes
    effect(() => {
      const categoryId = this.selectedCategoryId();
      if (categoryId) {
        this.updateBreadcrumbData(categoryId);
      }
    });

    // Reactive effect for theme styles - applies automatically when data changes
    effect(() => {
      if (this.categoryLayoutData()) {
        this.applyCategoryThemeStyle();
      }
    });

    // Reactive effect for store configuration changes
    effect(() => {
      const categoryConfig = this.storeConfigurationRepositoryService.categoryPageConfiguration();
      const searchConfig = this.storeConfigurationRepositoryService.searchPageConfiguration();
      const productsConfig = this.storeConfigurationRepositoryService.productsPageConfiguration();

      // Only update if we have a page type and configuration data
      if (this.currentPage && (categoryConfig || searchConfig || productsConfig)) {
        this.getLayoutPageConfiguration();
      }
    });

  }

  private detectPageType() {
    // Try multiple ways to detect the route
    const parentUrl = this.route.snapshot?.parent?.url;
    const currentUrl = this.route.snapshot?.url;
    const routeConfig = this.route.snapshot?.routeConfig;

    // Method 1: Check parent URL (for nested routes)
    let routePath = parentUrl?.[0]?.path;

    // Method 2: Check current URL if parent doesn't work
    if (!routePath && currentUrl?.length > 0) {
      routePath = currentUrl[0]?.path;
    }

    // Method 3: Check route config path
    if (!routePath && routeConfig?.path) {
      const pathSegments = routeConfig.path.split('/');
      routePath = pathSegments[0];
    }

    // Method 4: Parse from window.location as fallback
    if (!routePath) {
      const urlSegments = window.location.pathname.split('/').filter(segment => segment);
      if (urlSegments.length > 0) {
        routePath = urlSegments[0];
      }
    }

    this.detectedPage = routePath as DynamicPage;
    this.page = this.detectedPage;
    // Set searchedValue if on search route
    if (this.detectedPage === DynamicPage.SEARCH) {
      const queryParam = this.route.snapshot.queryParams;
      const searchValue = queryParam?.q || queryParam?.category_name_keywords || queryParam?.display_name_keywords || queryParam?.seo_detail_keywords;
      this.searchedValue.set(searchValue || '');
    } else {
      this.searchedValue.set('');
    }
  }

  ngOnInit(): void {
    this.announcement = this.announcementsService.getAnnouncement(
      AnnouncementBarPage.ProductListingPage
    ) || createDefaultAnnouncement();


    this.getLayoutPageConfiguration();


    // Differentiate based on primary route params (category,search)
    this.route.params
      .pipe(
        map(() => {
          // Try multiple ways to get the route type
          const parentPath = this.route?.snapshot?.parent?.url?.[0]?.path;
          const currentPath = this.route?.snapshot?.url?.[0]?.path;
          const routeConfig = this.route?.snapshot?.routeConfig?.path;

          return parentPath || currentPath || routeConfig;
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((filter_type) => {
        // Show shimmer immediately when route changes
        this.showPageLoader();

        // Reset initialization flag for new route
        this.isInitializing = true;

        // Reset products and pagination state on route change to prevent duplicate API calls
        this.fetchedProducts.set({ _scroll_id: '', filter: [], hits: { hits: [], max_score: 0, total: { value: 0, relation: 'eq' }, _scroll_id: '' } });
        this.listExhausted = false;
        this.pageNumber.set(0);
        this.isInitialLoad = true; // Set flag to prevent scroll pagination during initial load
        this.isUserScrolling = false; // Reset user scroll flag on route change
        if (this.platformService.isBrowser()) {
          this.lastScrollY = window.scrollY || window.pageYOffset;
        }

        // Re-detect page type and update searchedValue
        this.detectPageType();

        this.alphabetFilterControl.reset();
        let filters = {};

        switch (filter_type) {
          case 'search':
            const { q: query = '', tags = '', brand_id = '', brand_name = '', collection = null, ...additionalQueryParams } = this.route.snapshot.queryParams;

            const _collection = collection ? JSON.parse(collection) : null;

            const searchQuery = tags || brand_name || brand_id || _collection || additionalQueryParams?.display_name_keyword ||
              additionalQueryParams?.seo_detail_keyword || additionalQueryParams?.category_name_keyword || additionalQueryParams?.collection_ids || query;
            this.conversionTrackingService.trackSearchConversion(searchQuery);

            filters = {
              search: query,
              ...(!this.showOutOfStockProducts ? {
                in_stock: true
              } : {}),
              tags,
              ordering: '',
              ...(brand_id ? {
                brand_ids: brand_id
              } : {}),
              ...(!!collection ? {
                collection_ids: _collection?.id
              } : {}),
              ...(additionalQueryParams as any || {})
            };
            this.sortBy.patchValue('', { emitEvent: false });

            if (brand_name) {
              this.brandName$ = of(brand_name);
            }
            if (_collection) {
              this.collectionTitle$ = of(_collection?.name);
            }
            break;

          case 'category':
            const category_url_paths = this.route.snapshot.params?.url_path;
            filters = {
              // If showOutOfStockProducts value is false then in_stock set to true
              ...(!this.showOutOfStockProducts ? {
                in_stock: true
              } : {}),
              ...((category_url_paths && category_url_paths !== '') ? {
                category_url_paths
              } : {})
            };

            this.productsRepositoryService.getProductCategory(category_url_paths).then(() => {
              const category = this.productsRepositoryService.category();
              if (category) {
                this.seoService.updateSeoInformationForCategory(category);
                this.seoService.updateTitle(category?.name);
                this.getSelectedCategoryBreadCrumb(category?.id);
                this.selectedCategoryId.set(category.id);
                this.categoryBanners = category?.category_banners?.[0]?.sections?.slice()?.sort((section1, section2) => section1.position - section2.position) || [];
                this.categoryName = category?.name;
              }
            });
            break;

          default:
            break;
        }

        this.globalFiltersService.clearFilters();
        this.setFilters({ filters, refresh: true });

        // Mark initialization as complete to allow effects to run after a small delay
        setTimeout(() => {
          this.isInitializing = false;
        }, 100); // Small delay to prevent immediate effect execution

      });
    // Look for changes in queryParams
    this.route.queryParams.pipe(
      skip(1),
      takeUntil(this.destroyed$)
    ).subscribe(queryParams => {
      // Update searchedValue if on search route
      if (this.detectedPage === DynamicPage.SEARCH) {
        const searchValue = queryParams?.q || queryParams?.category_name_keywords || queryParams?.display_name_keywords || queryParams?.seo_detail_keywords;
        this.searchedValue.set(searchValue || '');
      }

      let filters: any = {};
      const _queryParams = Object.keys(queryParams);
      _queryParams.forEach(key => {
        if (key === 'q') {
          filters.search = queryParams[key];
          // If showOutOfStockProducts value is false then in_stock set to true
          if (!this.showOutOfStockProducts) {
            filters.in_stock = true;
          }
        } else if (key === 'tags') {
          filters.tags = queryParams[key];
        } else {
          filters[key] = queryParams[key];
        }
      });

      // remove existed suggestion key
      if (filters.hasOwnProperty(SUGGESTION_KEY.category_name_keywords)) {
        delete this.filters.value?.filters[SUGGESTION_KEY.display_name_keywords];
        delete this.filters.value?.filters[SUGGESTION_KEY.seo_detail_keywords];
      } else if (filters.hasOwnProperty(SUGGESTION_KEY.display_name_keywords)) {
        delete this.filters.value?.filters[SUGGESTION_KEY.category_name_keywords];
        delete this.filters.value?.filters[SUGGESTION_KEY.seo_detail_keywords];
      } else if (filters.hasOwnProperty(SUGGESTION_KEY.seo_detail_keywords)) {
        delete this.filters.value?.filters[SUGGESTION_KEY.category_name_keywords];
        delete this.filters.value?.filters[SUGGESTION_KEY.display_name_keywords];
      } else if (filters?.search) {
        delete this.filters.value?.filters[SUGGESTION_KEY.display_name_keywords];
        delete this.filters.value?.filters[SUGGESTION_KEY.seo_detail_keywords];
        delete this.filters.value?.filters[SUGGESTION_KEY.category_name_keywords];
      }

      filters = {
        ...this.filters.value?.filters,
        ...filters
      };

      // parse params
      const { brand_id = '', brand_name = '', collection = null } = queryParams || {};

      const _collection = collection ? JSON.parse(collection) : null;
      const searchQuery = brand_name || brand_id || _collection || filters?.tags || filters?.search || filters?.display_name_keyword ||
        filters?.seo_detail_keyword || filters?.category_name_keyword || '';
      this.conversionTrackingService.trackSearchConversion(searchQuery);

      if (brand_name) {
        this.brandName$ = of(brand_name);
      }
      if (_collection) {
        this.collectionTitle$ = of(_collection?.name);
      }

      filters = {
        ...filters,
        ...(brand_id && {
          brand_ids: brand_id
        }),
        ...(!!collection && {
          collection_ids: _collection?.id
        })
      };

      this.setFilters({ filters, refresh: true });
    });

    this.sortBy.valueChanges.pipe(
      startWith(this.sortBy.value), // Start with current value
      pairwise(), // Compare current with previous value
      filter(([prev, curr]) => prev !== curr), // Only emit when value actually changes
      map(([, curr]) => curr), // Get the current value
      takeUntil(this.destroyed$)
    ).subscribe(ordering => {
      const filters = {
        ...this.filters.value?.filters,
        ordering
      };
      this.setFilters({ filters, refresh: false });
    });

    this.filters$.pipe(
      // Filters where firing unnecessarily due to reset logic, debounce does not allow duplicate requests to pass
      debounceTime(50),
      takeUntil(this.destroyed$)
    ).subscribe(filters => {
      this.loadProducts(filters || undefined, filters?.refresh || false);

      // will clear the filters in the filter components
      if (filters?.refresh) {
        // this.resetFiltersService.resetFilters();
      }
    });

    // listen for end of page and fire request
    this.globalScrollService.scrolled$.pipe(
      debounceTime(300), // Debounce scroll events
      throttleTime(500), // Throttle to prevent excessive calls
      filter(() => {
        // Check if this is a real user scroll (not programmatic or auto-trigger)
        if (this.platformService.isBrowser()) {
          const currentScrollY = window.scrollY || window.pageYOffset;
          // Only allow pagination if scroll position actually changed (user scrolled)
          const scrollChanged = Math.abs(currentScrollY - this.lastScrollY) > 5;
          if (scrollChanged) {
            this.lastScrollY = currentScrollY;
            this.isUserScrolling = true;
          } else {
            // Reset flag if no actual scroll movement
            this.isUserScrolling = false;
          }
        }

        // Prevent pagination if:
        // 1. List is exhausted
        // 2. Already loading
        // 3. No products loaded yet (initial load)
        // 4. Still in initial load phase
        // 5. Not a real user scroll (prevent auto-trigger from Intersection Observer)
        const hasProducts = (this.fetchedProducts()?.hits?.hits?.length ?? 0) > 0;
        return !this.listExhausted && !this.listLoading() && !this.loading() && !this.pageLoading() && hasProducts && !this.isInitialLoad && this.isUserScrolling;
      }),
      map(() => this.pageNumber()),
      filter(number => !isNaN(number) && number >= 0),
      distinctUntilChanged((v1, v2) => {
        if (v1 === 0 && v2 === 0) {
          return false;
        }
        return v1 === v2;
      }),
      concatMap((pageNumber: number) => {
        const nextPage = pageNumber + 1;
        this.pageNumber.set(nextPage);
        this.showListLoader();
        const collectedUtmAndCodeDetails = this.adsManagementService.getCollectedUtmAndCodeDetails();
        const updatedFilters = {
          ...this.filters.value?.filters,
          ...(collectedUtmAndCodeDetails && Object.keys(collectedUtmAndCodeDetails)?.length && {
            attribution_data: JSON.stringify({
              ...collectedUtmAndCodeDetails
            })
          })
        };

        const params = new ListParams(this.alphabetFilterControl.value, '', '', '', nextPage, PAGE_SIZE, '', updatedFilters);

        return this.productsRepositoryService.getProductsV2(params).then(() => {
          const response = this.productsRepositoryService.productsV2();
          return response;
        }).catch(() => {
          return null;
        });
      }),
      takeUntil(this.destroyed$)
    ).subscribe((response: any) => {
      this.hideListLoader();
      if (response) {
        const currentProducts = this.fetchedProducts()?.hits?.hits ?? [];
        const fetchedProducts = response.hits.hits;
        const combinedListProducts = currentProducts.concat(fetchedProducts);
        const currentProductSearch = { ...this.fetchedProducts() };
        if (!currentProductSearch) {
        }
        currentProductSearch.hits.hits = combinedListProducts;
        this.fetchedProducts.set(currentProductSearch);

        if (fetchedProducts.length === 0) {
          this.listExhausted$.next(true);
          this.listExhausted = true;
        } else {
          this.listExhausted = false;
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.handleClickActions();
    this.appendInitialStyles();
    this.applyCategoryThemeStyle();
  }

  alphabetFilter(event: string | Event) {
    const value = typeof event === 'string' ? event : (event.target as HTMLInputElement)?.value || '';
    this.alphabetFilterControl.patchValue(value);
    this.loadProducts(this.filters.getValue() || undefined, false, value);
  }

  getSelectedCategoryBreadCrumb(categoryId: string) {
    this.selectedCategoryId.set(categoryId);
    // Note: The effect in constructor will call updateBreadcrumbData when selectedCategoryId changes
  }

  updateBreadcrumbData(categoryId: string) {
    const findCategoryPath = (categories: any, categoryId: string): any => {
      for (const category of categories) {
        // If the category is found by id, return the breadcrumb
        if (category.id === categoryId) {
          return [{ name: category.name, id: category.id, url: category.url_path }];
        }

        // If there are sub-categories, recursively search them
        if (category.sub_categories && category.sub_categories.length > 0) {
          const subCategoryPath: any = findCategoryPath(category.sub_categories, categoryId);
          if (subCategoryPath) {
            return [{ name: category.name, id: category.id, url: category.url_path }, ...subCategoryPath];
          }
        }
      }
      return null;
    };

    // Get body configurations and update breadcrumb data signal
    const bodyConfigurations = this.storeConfigurationRepositoryService.bodyConfigurations();

    if (bodyConfigurations?.categories) {
      // Find the full path for the given categoryId
      const categoryPath = findCategoryPath(bodyConfigurations.categories, categoryId);

      // Update breadcrumbData signal
      const breadcrumbData = categoryPath ? [...categoryPath] : [];
      this._breadcrumbData.set(breadcrumbData);
    }
  }

  handleClickActions() {
    // this.pageLoading = false;
    this.cdr.detectChanges();
    this.checkForClickActions();
  }

  // temp send this inside after view init without timer
  appendInitialStyles() {
    // Apply styles immediately
    const selectorRegex = /\.([^\s{]+)(?=\s*{)/g;

    // Array to store extracted selectors
    let selectors: string[] = [];

    // Iterate over each key in the categoryLayoutStyles object
    for (const key in this.categoryLayoutStyles) {
      // Use a type assertion to inform TypeScript that `key` is a key of categoryLayoutStyle
      const styleString = this.categoryLayoutStyles[key as keyof CategoryLayoutStyle];

      let match;
      // Match all selectors in the current CSS string
      while ((match = selectorRegex.exec(styleString)) !== null) {
        selectors.push(match[1]!); // Push the selector without the dot
      }

      // Use the first selector to modify the ID
      const modifiedId = selectors[0]?.replace('.', ''); // Remove dot to get id

      // Apply the style for the first selector found
      if (modifiedId) {
        this.document.getElementById(modifiedId)?.remove();
        this.appendStyle(modifiedId!, styleString, true);
        selectors = [];
      }
    }

    // Apply theme styles reactively
    this.applyCategoryThemeStyle();
  }


  applyCategoryThemeStyle() {
    const categoryThemeContainer = this.document.getElementsByClassName('dynamic-listing-page-theme')[0];
    const layoutData = this.categoryLayoutData();
    const themeBackgroundColor = layoutData?.categoryLayoutConfig?.themeBackgroundColor;
    const themeFontFamily = layoutData?.categoryLayoutConfig?.themeFontFamily;

    if (themeBackgroundColor && categoryThemeContainer) {
      this.renderer.setStyle(categoryThemeContainer, 'background-color', themeBackgroundColor);
    }

    if (themeFontFamily && categoryThemeContainer) {
      this.renderer.setStyle(categoryThemeContainer, 'font-family', themeFontFamily);
    }
  }

  private appendStyle(sectionId: string, styles: string, append: boolean = false) {
    let existingStyleTag;
    const existingStyleTags = this.document.head.querySelectorAll('style');
    existingStyleTags?.forEach((style) => {
      if (style?.getAttribute('id') === sectionId) {
        existingStyleTag = style;
      }
    });

    if (existingStyleTag) {
      this.renderer.setProperty(existingStyleTag, 'textContent', styles);
    } else if (append) {
      const modifiedKey = sectionId?.replace('style-', '');
      const timeoutId = setTimeout(() => {
        const sectionToAddClass = this.document.getElementById(modifiedKey);
        if (sectionToAddClass) {
          sectionToAddClass.classList.add(sectionId);
        }
      }, 2000);
      this.timeoutIds.push(timeoutId);

      const style = this.renderer.createElement('style');
      style.id = sectionId;
      const text = this.renderer.createText(styles || "");
      this.renderer.appendChild(style, text);
      this.renderer.appendChild(this.document.head, style);
    }
  }

  applyFilters(filters: FilterSelection[]) {

    // If no filters are applied (clearing filters), reset to base filters only
    if (!filters || filters.length === 0) {
      const baseFilters = this.filters?.value?.filters || {};
      // Remove filter-specific properties but keep base filters like search, category, etc.
      const { categories, attributes, selling_price, brands, ...baseOnlyFilters } = baseFilters;
      this.setFilters({ filters: baseOnlyFilters, refresh: false });
      return;
    }

    // preparing categories filter for query param
    const mappedCategory = filters?.map(categoryFilter => {
      if (categoryFilter.type === ProductFilterType.Category) {
        return categoryFilter?.filter.value;
      }
    }).filter(Boolean).join(',');

    // preparing attributes filter query param
    const attributesFilter = filters?.map(attributeFilter => {
      if (attributeFilter.type === ProductFilterType.Attribute) {
        return { [`${attributeFilter.label}`]: attributeFilter?.filter?.value };
      }
    }).filter(Boolean);

    const priceFilter = filters?.map(priceFilter => {
      if (priceFilter.type === ProductFilterType.Price) {
        return { lte: priceFilter?.filter?.price_range?.to, gte: priceFilter?.filter?.price_range?.from };
      }
    }).filter(Boolean);

    // preparing brand filter query param
    const brandsFilter = filters?.map(attributeFilter => {
      if (attributeFilter.type === ProductFilterType.Brand) {
        return attributeFilter?.filter?.value;
      }
    }).filter(Boolean)?.join(',');

    const attributes = {
      ...this.filters?.value?.filters,
      categories: mappedCategory,
      attributes: JSON.stringify(attributesFilter),
      selling_price: JSON.stringify(priceFilter),
      brands: brandsFilter
    };

    this.setFilters({ filters: attributes, refresh: false });
  }

  onSortByChange(sortValue: string) {
    // Update the sortBy form control to trigger the existing subscription
    this.sortBy.patchValue(sortValue, { emitEvent: true });
  }



  setFilters(filters: any) {
    this.filters.next(filters);
    this.pageNumber.set(0);
  }

  loadProducts(filtersApplied?: Filters, showPageLoader: boolean = false, alphabet_filter = this.alphabetFilterControl.value) {
    const { filters, refresh = false } = filtersApplied || {};
    const collectedUtmAndCodeDetails = this.adsManagementService.getCollectedUtmAndCodeDetails();
    const updatedFilters = {
      ...filters,
      ...(collectedUtmAndCodeDetails && Object.keys(collectedUtmAndCodeDetails)?.length && {
        attribution_data: JSON.stringify({
          ...collectedUtmAndCodeDetails
        })
      })
    };
    const params = new ListParams(alphabet_filter, '', '', '', 0, PAGE_SIZE, '', updatedFilters);

    this.showLoader();
    showPageLoader ? this.showPageLoader() : this.hidePageLoader();
    this.productsRepositoryService.getProductsV2(params).then(() => {
      const response = this.productsRepositoryService.productsV2();
      if (response) {
        if (refresh) {
          this.globalFiltersService.updateAggregations(response?.filter);
        }

        this.listExhausted = false;
        this.pageNumber.set(0);

        this.hideLoader();
        this.hidePageLoader();


        this.fetchedProducts.set(response);
        this.productsPaginationService.scrollId = response?._scroll_id;

        // Mark initial load as complete - now scroll pagination can work
        this.isInitialLoad = false;
      }
    }).catch((error: any) => {
      this.hideLoader();
      this.hidePageLoader();
      this.isInitialLoad = false; // Reset flag even on error
    });
  }


  getLayoutPageConfiguration() {
    // Check if store configuration is loaded
    const storeConfig = this.storeConfigurationRepositoryService.storeConfigurations();

    switch (this.currentPage) {
      case DynamicPage.CATEGORY:
        this.layoutPageConfiguration = this.storeConfigurationRepositoryService.categoryPageConfiguration();
        break;
      case DynamicPage.SEARCH:
        this.layoutPageConfiguration = this.storeConfigurationRepositoryService.searchPageConfiguration();
        break;
      case DynamicPage.PRODUCTS:
        this.layoutPageConfiguration = this.storeConfigurationRepositoryService.productsPageConfiguration();
        break;
      default:
        this.layoutPageConfiguration = null;
    }



    if (this.layoutPageConfiguration) {
      this.layoutPageConfiguration = JSON.parse(this.layoutPageConfiguration);


      // Initialize categoryLayoutData and categoryLayoutStyles from the configuration
      if (this.layoutPageConfiguration && this.layoutPageConfiguration.length > 0) {

        let apiDataConfiguration = null;
        let rawThemeData = null;
        let stylesData = null;

        // Search through the array to find the configuration object
        for (let i = 0; i < this.layoutPageConfiguration.length; i++) {
          const item = this.layoutPageConfiguration[i];

          // Check if this item is an array with configuration data
          if (Array.isArray(item) && item.length > 0) {
            // Look for the object with configuration properties
            for (let j = 0; j < item.length; j++) {
              const configItem = item[j];
              if (configItem && typeof configItem === 'object' && configItem.categoryLayoutConfig) {
                apiDataConfiguration = configItem;
                break;
              }
            }
          }
          // Check if this item contains theme data (themeFontFamily, themeBackgroundColor)
          else if (item && typeof item === 'object' && (item.themeFontFamily || item.themeBackgroundColor)) {
            rawThemeData = item;
          }
          // Check if this item contains styles data
          else if (item && typeof item === 'object' && (item.toolBarStyle || item.paginationStyle || item.productLayoutStyle)) {
            stylesData = item;
          }
        }

        // Use only API data - no fallbacks to default data
        const finalCategoryLayoutConfig = {
          ...apiDataConfiguration?.categoryLayoutConfig,
          ...rawThemeData  // This will override with your updated theme values
        };

        const layoutData = {
          categoryLayoutConfig: finalCategoryLayoutConfig,
          bannerConfiguration: apiDataConfiguration?.bannerConfiguration,
          filterData: apiDataConfiguration?.filterData,
          toolBarData: apiDataConfiguration?.toolBarData,
          paginationData: apiDataConfiguration?.paginationData,
          productCardData: apiDataConfiguration?.productCardData,
          breadCrumbData: apiDataConfiguration?.breadCrumbData,
          commonData: apiDataConfiguration?.commonData,
          alphabetFilterData: apiDataConfiguration?.alphabetFilterData
        } as CategoryLayoutData;

        this._categoryLayoutData.set(layoutData);

        this.categoryLayoutStyles = stylesData || {}; // Styles data

        setTimeout(() => {
          this.applyCategoryThemeStyle();
        }, 0);
      }
    }

    this.listingPageService.renderPage(this.renderer, this.layoutPageConfiguration);
  }

  private showPageLoader() {
    this.pageLoading.set(true);
  }

  private hidePageLoader() {
    this.pageLoading.set(false);
  }

  private showLoader() {
    this.loading.set(true);
  }

  private hideLoader() {
    this.loading.set(false);
  }

  private showListLoader() {
    this.listLoading.set(true);
  }

  private hideListLoader() {
    this.listLoading.set(false);
  }

  ngOnDestroy() {
    this.conversionTrackingService.removeConversionTracking('Search');
    this.destroyed$.next(true);
    this.destroyed$.complete();
    // Clear all timeouts to prevent memory leaks
    this.timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
    this.timeoutIds = [];
    // No need to complete signals
  }
}
